import {
  Column,
  ColumnProps,
  ColumnFilterElementTemplateOptions,
} from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { TFunction } from 'i18next';
import { formatDateAndTime } from '@/utils/formatDate';

import InputDateFilter from '@/components/InputDateFilter/InputDateFilter';

import {
  filterApplyTemplate,
  filterClearTemplate,
  inputTextTemplate,
} from '@/components/Table/Table';

import ModalChart from '../ModalChart';
import { Schedule } from '../types';
import styles from './ScheduleTable.module.scss';

export const tableColumns: ColumnProps[] = [
  {
    field: 'codigo',
    header: '#',
    filter: true,
    sortable: true,
    showFilterMenu: true,
  },
  {
    field: 'nombre',
    header: 'Buque',
    className: styles.shipColumn,
    filter: true,
    sortable: true,
    showFilterMenu: true,
  },
  {
    field: 'servicio',
    header: 'Servicio',
    filter: true,
    sortable: true,
    showFilterMenu: true,
  },
  {
    field: 'comienzoOperaciones',
    header: 'Inicio',
    filter: true,
    body: (rowData: Schedule) => formatDateAndTime(rowData.comienzoOperaciones),
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'finOperaciones',
    header: 'Fin',
    body: (rowData: Schedule) => formatDateAndTime(rowData.finOperaciones),
  },
  {
    field: 'fechaFinDescarga',
    header: 'Entrega import',
    body: (rowData: Schedule) => formatDateAndTime(rowData.fechaFinDescarga),
  },
  {
    field: 'aperturaStacking',
    header: 'Apertura',
    body: (rowData: Schedule) => formatDateAndTime(rowData.aperturaStacking),
  },
  {
    field: 'cierreStacking',
    header: 'Cierre',
    body: (rowData: Schedule) => formatDateAndTime(rowData.cierreStacking),
  },
  {
    field: 'cutOff',
    header: 'Ingreso tardío hasta',
    body: (rowData: Schedule) => formatDateAndTime(rowData.cutOff),
  },
  {
    field: 'modalChart',
    header: '',
    body: (rowData: Schedule) => {
      return (
        <ModalChart
          codViaje={rowData.codigo}
          buqueName={rowData.nombre}
          armador={rowData?.armador}
          servicio={rowData?.servicio}
          operaciones={rowData?.comienzoOperaciones}
        />
      );
    },
  },
];

export const getHeaderColumnsGroup = (
  translates: TFunction<'schedule', undefined>,
  showDetail?: boolean
) => (
  <ColumnGroup>
    <Row>
      <Column
        header="#"
        field="codigo"
        rowSpan={3}
        sortable
        style={{ borderTopLeftRadius: '16px' }}
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />
      <Column
        header={translates('tableHeadings.ship')}
        field="nombre"
        rowSpan={3}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />
      <Column
        header={translates('tableHeadings.service')}
        field="servicio"
        rowSpan={3}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />
    </Row>
    <Row>
      <Column
        field="comienzoOperaciones"
        header={translates('tableHeadings.operations')}
        colSpan={3}
      />
      <Column
        header={translates('tableHeadings.stacking')}
        colSpan={4}
        style={{ borderTopRightRadius: '16px' }}
      />
    </Row>
    <Row>
      <Column
        header={translates('tableHeadings.start')}
        field="comienzoOperaciones"
        sortable
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <InputDateFilter options={options} />
        )}
        dataType="date"
        filterField="comienzoOperaciones"
      />
      <Column
        header={translates('tableHeadings.end')}
        field="finOperaciones"
        filterField="finOperaciones"
        sortable
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <InputDateFilter options={options} />
        )}
        dataType="date"
      />
      <Column
        header={translates('tableHeadings.importDelivery')}
        field="fechaFinDescarga"
        filterField="fechaFinDescarga"
        sortable
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <InputDateFilter options={options} />
        )}
        dataType="date"
        body={(rowData) =>
          rowData.fechaFinDescarga
            ? formatDateAndTime(rowData.fechaFinDescarga)
            : '-'
        }
      />
      <Column
        header={translates('tableHeadings.opening')}
        field="aperturaStacking"
        filterField="aperturaStacking"
        sortable
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <InputDateFilter options={options} />
        )}
        dataType="date"
      />
      <Column
        header={translates('tableHeadings.closing')}
        field="cierreStacking"
        filterField="cierreStacking"
        sortable
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <InputDateFilter options={options} />
        )}
        dataType="date"
      />
      <Column
        header={translates('tableHeadings.lateEntry')}
        field="cutOff"
        filterField="cutOff"
        sortable
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <InputDateFilter options={options} />
        )}
        dataType="date"
      />
      {!!showDetail && <Column field="cutOff" sortable />}
    </Row>
  </ColumnGroup>
);
